import { ArrowLeft, Globe, TrendingUp, Zap, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export default function AboutUs() {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Link to="/" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-[#E5B33E]" style={{ fontFamily: 'Manrope' }}>
              About MudraFX
            </CardTitle>
            <p className="text-sm text-muted-foreground">Modern Currency Conversion for the Digital Age</p>
          </CardHeader>
          <CardContent className="prose prose-invert max-w-none space-y-6">
            <section>
              <p className="text-lg text-muted-foreground leading-relaxed">
                MudraFX is a modern, fast, and reliable currency conversion tool designed to provide real-time exchange rates for users in India and globally. Built with cutting-edge technology, MudraFX makes currency conversion simple, accurate, and accessible to everyone.
              </p>
            </section>

            <section className="bg-muted/50 rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold text-foreground mb-4 flex items-center gap-2">
                <Globe className="w-6 h-6 text-primary" />
                Our Mission
              </h2>
              <p className="text-muted-foreground">
                To empower individuals and businesses with instant access to accurate currency exchange information, enabling informed financial decisions in an increasingly connected global economy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Why Choose MudraFX?</h2>
              <div className="grid gap-4">
                <div className="flex gap-4 items-start p-4 bg-card border border-border rounded-lg">
                  <div className="bg-primary/20 p-2 rounded-lg">
                    <Zap className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Lightning Fast</h3>
                    <p className="text-sm text-muted-foreground">
                      Real-time exchange rates for 166+ currencies with instant conversion results. No delays, no waiting.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start p-4 bg-card border border-border rounded-lg">
                  <div className="bg-primary/20 p-2 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Historical Data</h3>
                    <p className="text-sm text-muted-foreground">
                      Access 7-day and 30-day historical exchange rate trends with interactive charts. Make informed decisions based on past data.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start p-4 bg-card border border-border rounded-lg">
                  <div className="bg-primary/20 p-2 rounded-lg">
                    <Shield className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Reliable & Accurate</h3>
                    <p className="text-sm text-muted-foreground">
                      Powered by trusted data sources (ExchangeRate-API and Frankfurter API) with European Central Bank rates.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Key Features</h2>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="bg-muted/30 p-3 rounded-lg border border-border">
                  <p className="text-sm font-medium text-foreground">✓ 166+ Global Currencies</p>
                </div>
                <div className="bg-muted/30 p-3 rounded-lg border border-border">
                  <p className="text-sm font-medium text-foreground">✓ Real-time Exchange Rates</p>
                </div>
                <div className="bg-muted/30 p-3 rounded-lg border border-border">
                  <p className="text-sm font-medium text-foreground">✓ Interactive Historical Charts</p>
                </div>
                <div className="bg-muted/30 p-3 rounded-lg border border-border">
                  <p className="text-sm font-medium text-foreground">✓ Favorite Currency Pairs</p>
                </div>
                <div className="bg-muted/30 p-3 rounded-lg border border-border">
                  <p className="text-sm font-medium text-foreground">✓ Conversion History</p>
                </div>
                <div className="bg-muted/30 p-3 rounded-lg border border-border">
                  <p className="text-sm font-medium text-foreground">✓ Mobile Responsive Design</p>
                </div>
                <div className="bg-muted/30 p-3 rounded-lg border border-border">
                  <p className="text-sm font-medium text-foreground">✓ Flag Emojis & Country Names</p>
                </div>
                <div className="bg-muted/30 p-3 rounded-lg border border-border">
                  <p className="text-sm font-medium text-foreground">✓ Volatility Analysis</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">What Makes Us Different?</h2>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <span><strong className="text-foreground">Visual Design:</strong> Clean, modern interface with dark theme and professional fintech aesthetic</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <span><strong className="text-foreground">Smart Features:</strong> Quick access favorites with live rates, one-click loading, and swap functionality</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <span><strong className="text-foreground">Data Transparency:</strong> See highest, lowest, and average rates over time periods</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <span><strong className="text-foreground">No Hidden Costs:</strong> Completely free to use with no subscriptions or premium tiers</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Technology Stack</h2>
              <p className="text-muted-foreground mb-3">
                MudraFX is built using modern web technologies to ensure speed, reliability, and security:
              </p>
              <div className="grid md:grid-cols-3 gap-3">
                <div className="bg-card border border-border rounded-lg p-3 text-center">
                  <p className="text-xs text-muted-foreground mb-1">Frontend</p>
                  <p className="font-semibold text-foreground">React.js</p>
                </div>
                <div className="bg-card border border-border rounded-lg p-3 text-center">
                  <p className="text-xs text-muted-foreground mb-1">Backend</p>
                  <p className="font-semibold text-foreground">FastAPI</p>
                </div>
                <div className="bg-card border border-border rounded-lg p-3 text-center">
                  <p className="text-xs text-muted-foreground mb-1">Database</p>
                  <p className="font-semibold text-foreground">MongoDB</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Our Commitment</h2>
              <p className="text-muted-foreground">
                We are committed to maintaining the highest standards of data accuracy, user privacy, and service reliability. MudraFX is continuously updated with new features and improvements based on user feedback.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Target Audience</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong className="text-foreground">Travelers:</strong> Planning international trips and need quick currency conversions</li>
                <li><strong className="text-foreground">Forex Traders:</strong> Monitoring exchange rate trends and volatility</li>
                <li><strong className="text-foreground">International Shoppers:</strong> Converting prices from foreign e-commerce sites</li>
                <li><strong className="text-foreground">Business Professionals:</strong> Managing international transactions and invoices</li>
                <li><strong className="text-foreground">Students:</strong> Understanding currency markets and financial concepts</li>
              </ul>
            </section>

            <section className="border-t border-border pt-6">
              <h2 className="text-2xl font-semibold text-foreground mb-4">Contact Us</h2>
              <p className="text-muted-foreground mb-3">
                We value your feedback and are here to help with any questions or suggestions.
              </p>
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Email Support</p>
                <p className="text-lg font-semibold">
                  <a href="mailto:support@mudrafx.app" className="text-primary hover:underline">support@mudrafx.app</a>
                </p>
              </div>
            </section>

            <section className="bg-[#E5B33E]/10 border border-[#E5B33E]/20 rounded-lg p-6 text-center">
              <p className="text-foreground font-semibold mb-2" style={{ fontFamily: 'Manrope' }}>Built with ❤️ in India</p>
              <p className="text-sm text-muted-foreground">MudraFX - Your trusted currency conversion partner</p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}